/**
 * ResonantFormationSkillEffect — Conjuration Tether path upgrade: formation root.
 *
 * When two or more shades hit the same enemy within a configurable time window
 * (default 500ms), the target is rooted for 800ms. Root prevents movement but
 * not attacks — the target can still fight back, just can't flee.
 *
 * The core mechanic lives in the embedded FormationRootTracker: a hit log keyed
 * by enemy ID, each entry recording which shade hit and when. On each new shade
 * hit, the tracker counts distinct shades that struck the same target within
 * the window. If that count meets or exceeds `formationRootMinShades`, a root
 * proc fires and all entries for that target are consumed.
 *
 * This root is independent of the Synapse twin-attack stagger. Both can fire
 * from the same hit sequence — a 2-shade volley within 400ms triggers Synapse
 * stagger AND Resonant Formation root. Both are applied as separate status
 * effects; their interaction emerges from the CC hierarchy (ADR-065).
 *
 * Integration hooks:
 *   - recordShadeHit(shadeId, targetId, timestampMs) — called by the integration
 *     layer (or CombatHitResolver) whenever a shade attack connects.
 *   - Returns { rootProc: true, rootDurationMs } when the formation condition
 *     fires, or { rootProc: false } when it doesn't.
 *   - The caller applies root through StatusEffectTracker.apply('root', ...).
 *
 * UpgradeEffectResolver keys (all on 'fracture' scope via enhancesId):
 * - formationRootDurationMs: additive, base 800
 * - formationRootWindowMs: max, base 500
 * - formationRootMinShades: max, base 2
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see conjuration-spec.md § Resonant Formation (fracture_upgrade_2)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ResonantFormationConfig {
  /** Duration of root CC in ms. */
  readonly formationRootDurationMs: number;
  /** Window in ms within which shade hits are counted. */
  readonly formationRootWindowMs: number;
  /** Minimum distinct shades that must hit to trigger root. */
  readonly formationRootMinShades: number;
}

const DEFAULT_CONFIG: ResonantFormationConfig = {
  formationRootDurationMs: 800,
  formationRootWindowMs: 500,
  formationRootMinShades: 2,
};

export interface FormationRootResult {
  readonly rootProc: boolean;
  /** Present when rootProc is true. */
  readonly rootDurationMs?: number;
}

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface ResonantFormationDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
  };
}

// ---------------------------------------------------------------------------
// Hit log entry
// ---------------------------------------------------------------------------

interface ShadeHitEntry {
  readonly shadeId: string;
  readonly timestampMs: number;
}

// ---------------------------------------------------------------------------
// Formation Root Tracker
// ---------------------------------------------------------------------------

/**
 * Tracks recent shade hits against individual enemies, detecting when
 * enough distinct shades strike within the configured window to trigger root.
 *
 * Hit entries are consumed on proc and expire passively when they fall
 * outside the window, preventing ghost matches across distant hits.
 */
export class FormationRootTracker {
  /** Hit log per enemy — array of (shadeId, timestamp) pairs. */
  private readonly hitLog = new Map<string, ShadeHitEntry[]>();

  /** Running count of root procs fired (observable for testing). */
  private _rootProcCount = 0;

  constructor(private readonly config: ResonantFormationConfig) {}

  get rootProcCount(): number { return this._rootProcCount; }

  /**
   * Record a shade hit against a target. Checks whether enough distinct
   * shades have hit the same target within the window.
   */
  recordHit(
    shadeId: string,
    targetId: string,
    timestampMs: number,
    windowMs: number,
    minShades: number,
    rootDurationMs: number,
  ): FormationRootResult {
    // Get or create the hit log for this target
    let entries = this.hitLog.get(targetId);
    if (!entries) {
      entries = [];
      this.hitLog.set(targetId, entries);
    }

    // Expire stale entries outside the window
    const cutoff = timestampMs - windowMs;
    const fresh = entries.filter(e => e.timestampMs > cutoff);

    // Add the new hit
    fresh.push({ shadeId, timestampMs });
    this.hitLog.set(targetId, fresh);

    // Count distinct shade IDs within the window
    const distinctShades = new Set(fresh.map(e => e.shadeId));

    if (distinctShades.size >= minShades) {
      // Root fires — consume all entries for this target
      this.hitLog.delete(targetId);
      this._rootProcCount++;
      return { rootProc: true, rootDurationMs };
    }

    return { rootProc: false };
  }

  /** Clear all tracked hits (e.g., on skill destruction). */
  clear(): void {
    this.hitLog.clear();
  }
}

// ---------------------------------------------------------------------------
// Resolver scope
// ---------------------------------------------------------------------------

/** Resolver scope — 'fracture' for upgrades with enhancesId: 'fracture'. */
const RESOLVER_SCOPE = 'fracture';

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ResonantFormationSkillEffect implements SkillEffect {
  readonly skillId = 'fracture_upgrade_2';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  readonly tracker: FormationRootTracker;

  private readonly deps: ResonantFormationDeps;
  private readonly config: ResonantFormationConfig;

  constructor(deps: ResonantFormationDeps, config: Partial<ResonantFormationConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.tracker = new FormationRootTracker(this.config);
  }

  // --- Resolver-backed getters ---

  /**
   * Resolve the effective root duration in ms. Additive stacking:
   * base 800 plus any bonuses from future root-extending nodes.
   */
  getResolvedRootDurationMs(): number {
    const bonus = this.deps.resolver.getAdditiveEffect(RESOLVER_SCOPE, 'formationRootDurationMs');
    return Math.max(0, this.config.formationRootDurationMs + bonus);
  }

  /**
   * Resolve the effective window in ms. Max aggregation: the JSON value
   * (500) is the baseline; future nodes could widen it.
   */
  getResolvedWindowMs(): number {
    return this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'formationRootWindowMs')
      ?? this.config.formationRootWindowMs;
  }

  /**
   * Resolve the minimum shade count for root proc. Max aggregation:
   * base 2 — future nodes could raise the threshold (unlikely but possible).
   */
  getResolvedMinShades(): number {
    return this.deps.resolver.getMaxEffect(RESOLVER_SCOPE, 'formationRootMinShades')
      ?? this.config.formationRootMinShades;
  }

  // --- Integration hook ---

  /**
   * Record a shade hit against a target. Called by the integration layer
   * whenever a shade attack connects. Returns whether the formation root
   * condition was met.
   *
   * @param shadeId     Unique ID of the shade that dealt the hit
   * @param targetId    Unique ID of the enemy that was hit
   * @param timestampMs Current simulation/game time in ms
   * @returns FormationRootResult — if rootProc is true, caller should apply
   *          root via StatusEffectTracker.apply('root', { durationMs: rootDurationMs })
   */
  recordShadeHit(shadeId: string, targetId: string, timestampMs: number): FormationRootResult {
    const windowMs = this.getResolvedWindowMs();
    const minShades = this.getResolvedMinShades();
    const rootDurationMs = this.getResolvedRootDurationMs();
    return this.tracker.recordHit(shadeId, targetId, timestampMs, windowMs, minShades, rootDurationMs);
  }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // upgrade — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Upgrade — formation root detection is event-driven, not tick-driven
  }

  destroy(): void {
    this.tracker.clear();
  }
}
